import { Box } from '@mui/material';
import axios from 'axios';
import React, { useCallback, useMemo, useState } from 'react';
import AuthField from './components/AuthField';
import AuthForm from './components/AuthForm';
import AuthPageWrapper from './components/AuthPageWrapper';

export const colors = { background: 'white', primary: '#345784', text: 'black' };

export default function AuthPage({ navTo, resetConfig }) {
  const [phase, setPhase] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'error' });
  const [isLoading, setIsLoading] = useState(false);

  const validateEmail = (v) =>
    v ? (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? null : 'Invalid email') : 'Email is required';
  const validatePassword = (v) => (v ? null : 'Password is required');
  const validateCode = (v) =>
    v ? (`${v}`.trim().length === 6 ? null : 'Code must be 6 characters') : 'Code required';

  const handleCancel = () => {
    setEmail('');
    setPassword('');
    setCode('');
    setAlert({ open: false, message: '', severity: 'error' });
    setPhase('login');
  };

  const post = useCallback(
    async (e, url, body, ok, bad) => {
      e.preventDefault();
      setAlert((p) => ({ ...p, open: false }));
      setIsLoading(true);
      resetConfig();
      try {
        const { data } = await axios.post(url, body, { validateStatus: () => true });
        data.status === 'success' ? ok(data) : bad(data?.error);
      } catch {
        setAlert({ open: true, message: 'Server error. Try again later.', severity: 'error' });
      } finally {
        setIsLoading(false);
      }
    },
    [resetConfig]
  );

  const sendCode = (e) =>
    post(
      e,
      '/sendLoginCode',
      { email, password },
      () => {
        setAlert({ open: true, message: 'Verification code sent.', severity: 'success' });
        setPhase('verify');
      },
      (err) => setAlert({ open: true, message: err || 'Login failed.', severity: 'error' })
    );

  const verify = (e) =>
    post(
      e,
      '/verifyLoginCode',
      { email, code },
      ({ redirect }) => {
        setAlert({ open: true, message: 'Login successful.', severity: 'success' });
        setLoginSuccess(true);
        navTo(redirect === '/changePassword' ? 'changePassword' : 'main.root');
      },
      (err) => {
        if (err?.toLowerCase().includes('maximum') || err?.toLowerCase().includes('expired'))
          handleCancel();
        else setCode('');
        setAlert({ open: true, message: err || 'Invalid or expired code.', severity: 'error' });
      }
    );

  const escapedEmail = useMemo(() => {
    if (!email) return '';
    const first = email[0];
    const endLen = email.length > 12 ? 8 : Math.floor(email.length / 3);
    const middle = email.slice(1, -endLen).replaceAll(/[^@]/g, '*');
    return `${first}${middle}${email.slice(-endLen)}`;
  }, [email]);

  return (
    <AuthPageWrapper alert={alert} onCloseAlert={() => setAlert((p) => ({ ...p, open: false }))}>
      {phase === 'login' && (
        <AuthForm
          disableSubmit={validateEmail(email) || validatePassword(password)}
          isLoading={isLoading}
          onCancel={handleCancel}
          onSubmit={sendCode}
          sectionTitle="Sign In To Continue"
          submitText={isLoading ? 'Sending...' : 'Send Code'}
        >
          <AuthField
            disabled={isLoading || loginSuccess}
            label="Email Address"
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            value={email}
          />
          <AuthField
            helperLinkText={'Forgot Password?'}
            helpLinkOnClick={() => navTo('forgotPassword')}
            disabled={isLoading || loginSuccess}
            label="Password"
            onChange={(e) => setPassword(e.target.value)}
            type="password"
            value={password}
          />
        </AuthForm>
      )}

      {phase === 'verify' && (
        <Box>
          <AuthForm
            disableCancel={loginSuccess}
            disableSubmit={validateCode(code) || loginSuccess}
            isLoading={isLoading}
            linkButtonText="Request New Code"
            linkLabel="Didn’t receive a code?"
            onCancel={handleCancel}
            onLinkButtonClick={sendCode}
            onSubmit={verify}
            sectionTitle="Enter Verification Code"
            submitText={isLoading ? 'Verifying...' : loginSuccess ? 'Signing in' : 'Submit'}
            subtitle={`A code has been sent to ${escapedEmail}`}
          >
            <AuthField
              label="Verification Code"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              textAlign="center"
              maxLength={6}
              disabled={isLoading || loginSuccess}
            />
          </AuthForm>
        </Box>
      )}
    </AuthPageWrapper>
  );
}
